import React, { useState } from 'react';
import { format } from 'date-fns';
import { X, Send, CheckCircle, Clock, UserCheck, MessageSquare, AlertCircle, Loader2, Monitor, Wifi, Printer, Smartphone, Package, ArrowRight, Calendar, Paperclip, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import ticketService from '../services/tickets';
import DeleteConfirmModal from './DeleteConfirmModal';

const STATUS_COLORS = {
  'Open': 'bg-red-100 text-red-700 border-red-200',
  'In Progress': 'bg-yellow-100 text-yellow-700 border-yellow-200',
  'Resolved': 'bg-green-100 text-green-700 border-green-200',
  'Closed': 'bg-gray-100 text-gray-600 border-gray-200',
};

const PRIORITY_COLORS = {
  'Low': 'bg-blue-100 text-blue-700 border-blue-200',
  'Medium': 'bg-yellow-100 text-yellow-700 border-yellow-200',
  'High': 'bg-orange-100 text-orange-700 border-orange-200',
  'Urgent': 'bg-red-100 text-red-700 border-red-200',
};

const STATUS_ACTIONS = [
  { from: 'Open', to: 'In Progress', label: 'Start Processing', icon: Clock, color: 'from-yellow-500 to-yellow-600' },
  { from: 'In Progress', to: 'Resolved', label: 'Mark Resolved', icon: CheckCircle, color: 'from-green-500 to-green-600' },
  { from: 'Resolved', to: 'Closed', label: 'Close Ticket', icon: X, color: 'from-gray-500 to-gray-600' },
];

const TicketDetail = ({ ticket, onClose, onUpdate, users = [] }) => {
  const { user } = useAuth();
  const [noteText, setNoteText] = useState('');
  const [resolution, setResolution] = useState(ticket.resolution || '');
  const [submitting, setSubmitting] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(ticket.assignedTo?.id || '');

  const isAdminUser = user?.role === 'admin';
  const isReporter = ticket.reportedBy?.id === user?.id;

  const getDeviceIcon = (deviceType) => {
    const icons = { Komputer: Monitor, Printer: Printer, Jaringan: Wifi, Software: Smartphone, Telepon: Smartphone, Lainnya: Package };
    const Icon = icons[deviceType] || Package;
    return Icon;
  };
  const DeviceIcon = getDeviceIcon(ticket.deviceType);

  const handleAddNote = async () => {
    if (!noteText.trim() || submitting) return;
    setSubmitting(true);
    try {
      const updated = await ticketService.addNote(ticket._id, noteText.trim());
      setNoteText('');
      onUpdate(updated);
    } catch (err) {
      alert(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const updated = await ticketService.updateTicketStatus(ticket._id, newStatus, resolution);
      onUpdate(updated);
    } catch (err) {
      alert(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedUserId || assigning) return;
    setAssigning(true);
    try {
      const selectedUser = users.find(u => u._id === selectedUserId);
      if (selectedUser) {
        const updated = await ticketService.assignTicket(ticket._id, {
          userId: selectedUser._id,
          username: selectedUser.username,
          name: selectedUser.name || selectedUser.username,
        });
        onUpdate(updated);
      }
    } catch (err) {
      alert(err.message);
    } finally {
      setAssigning(false);
    }
  };

  const handleDeleteTicket = async () => {
    if (isDeleting) return;
    setIsDeleting(true);
    try {
      await ticketService.deleteTicket(ticket._id);
      setDeleteConfirm(false);
      onClose();
    } catch (err) {
      alert(err.message);
      setDeleteConfirm(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const availableActions = STATUS_ACTIONS.filter(a => a.from === ticket.status);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto animate-fade-in" onClick={onClose}>
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20">
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity" />
        <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-auto z-10 animate-scale-in" onClick={e => e.stopPropagation()}>
          {/* Header */}
          <div className="flex items-start justify-between px-6 py-5 border-b border-gray-100">
            <div className="flex items-center space-x-3">
              <div className={`p-2.5 rounded-xl ${
                ticket.status === 'Open' ? 'bg-red-50' :
                ticket.status === 'In Progress' ? 'bg-yellow-50' :
                ticket.status === 'Resolved' ? 'bg-green-50' : 'bg-gray-50'
              }`}>
                <DeviceIcon className={`w-6 h-6 ${
                  ticket.status === 'Open' ? 'text-red-500' :
                  ticket.status === 'In Progress' ? 'text-yellow-600' :
                  ticket.status === 'Resolved' ? 'text-green-600' : 'text-gray-500'
                }`} />
              </div>
              <div>
                <div className="flex items-center space-x-2">
                  <span className="text-xs font-mono text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{ticket.ticketNumber}</span>
                  <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full border ${PRIORITY_COLORS[ticket.priority]}`}>{ticket.priority}</span>
                  <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full border ${STATUS_COLORS[ticket.status]}`}>{ticket.status}</span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mt-1">{ticket.title}</h3>
              </div>
            </div>
            <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="px-6 py-5 space-y-6 max-h-[60vh] overflow-y-auto">
            {/* Info Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div className="bg-gray-50 rounded-xl p-3">
                <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">Department</p>
                <p className="text-sm font-semibold text-gray-900 mt-0.5">{ticket.bagian}</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-3">
                <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">Device</p>
                <p className="text-sm font-semibold text-gray-900 mt-0.5">{ticket.deviceType}</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-3">
                <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">Reported By</p>
                <p className="text-sm font-semibold text-gray-900 mt-0.5">{ticket.reportedBy?.name || ticket.reportedBy?.username}</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-3">
                <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">Tanggal</p>
                <p className="text-sm font-semibold text-gray-900 mt-0.5">
                  {ticket.tanggal ? format(new Date(ticket.tanggal), 'dd MMM yyyy') : '-'}
                </p>
              </div>
              <div className="bg-gray-50 rounded-xl p-3">
                <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">Created</p>
                <p className="text-sm font-semibold text-gray-900 mt-0.5">{format(new Date(ticket.createdAt), 'MMM dd, HH:mm')}</p>
              </div>
            </div>

            {/* Description */}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Description</h4>
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{ticket.description}</p>
                {ticket.lampiran && (
                  <a
                    href={`http://localhost:5000${ticket.lampiran}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 mt-3 text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    <Paperclip className="w-4 h-4" />
                    <span>View Attachment</span>
                  </a>
                )}
              </div>
            </div>

            {/* Resolution (if resolved) */}
            {ticket.status === 'Resolved' && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Resolution</h4>
                <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{ticket.resolution || 'No resolution details provided.'}</p>
                  {ticket.resolvedAt && (
                    <p className="text-xs text-green-600 mt-2">Resolved on {format(new Date(ticket.resolvedAt), 'MMM dd, yyyy HH:mm')}</p>
                  )}
                </div>
              </div>
            )}

            {/* Assignment (admin) */}
            {isAdminUser && ticket.status !== 'Closed' && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Assignment</h4>
                <div className="flex items-center space-x-2">
                  <select
                    value={selectedUserId}
                    onChange={(e) => setSelectedUserId(e.target.value)}
                    className="select-field text-sm flex-1"
                  >
                    <option value="">-- Select user to assign --</option>
                    {users.filter(u => u.role !== 'viewer').map(u => (
                      <option key={u._id} value={u._id}>
                        {u.name || u.username} ({u.role})
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={handleAssign}
                    disabled={!selectedUserId || assigning}
                    className="btn-primary text-sm whitespace-nowrap"
                  >
                    {assigning ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserCheck className="w-4 h-4" />}
                    <span>Assign</span>
                  </button>
                </div>
                {ticket.assignedTo?.name && (
                  <p className="text-xs text-gray-500 mt-1">Currently assigned to: <strong>{ticket.assignedTo.name}</strong></p>
                )}
              </div>
            )}

            {/* Status Actions (admin) */}
            {isAdminUser && availableActions.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Actions</h4>
                <div className="space-y-2">
                  {ticket.status === 'In Progress' && (
                    <textarea
                      placeholder="Add resolution notes before resolving..."
                      value={resolution}
                      onChange={(e) => setResolution(e.target.value)}
                      rows={2}
                      className="input-field resize-none text-sm"
                    />
                  )}
                  <div className="flex flex-wrap gap-2">
                    {availableActions.map(action => (
                      <button
                        key={action.to}
                        onClick={() => handleStatusChange(action.to)}
                        disabled={submitting}
                        className={`px-4 py-2 text-white text-sm font-semibold rounded-xl bg-gradient-to-r ${action.color} hover:shadow-md transition-all flex items-center space-x-1.5 disabled:opacity-50`}
                      >
                        {submitting ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <action.icon className="w-4 h-4" />
                        )}
                        <span>{action.label}</span>
                        <ArrowRight className="w-3 h-3" />
                      </button>
                    ))}
                    {ticket.status !== 'Closed' && ticket.status !== 'Open' && (
                      <button
                        onClick={() => handleStatusChange('Open')}
                        disabled={submitting}
                        className="px-4 py-2 text-sm font-semibold rounded-xl border border-gray-300 text-gray-600 hover:bg-gray-50 transition-all flex items-center space-x-1.5 disabled:opacity-50"
                      >
                        <X className="w-4 h-4" />
                        <span>Reopen</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Notes / Comments */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-gray-700 flex items-center space-x-1.5">
                  <MessageSquare className="w-4 h-4" />
                  <span>Notes & Updates ({ticket.notes?.length || 0})</span>
                </h4>
              </div>

              <div className="space-y-3 mb-4">
                {ticket.notes && ticket.notes.length > 0 ? (
                  [...ticket.notes].reverse().map((note, idx) => (
                    <div key={idx} className="bg-gray-50 rounded-xl p-3">
                      <div className="flex items-start justify-between mb-1">
                        <div className="flex items-center space-x-2">
                          <span className="text-xs font-semibold text-gray-700">
                            {note.author?.name || note.author?.username}
                          </span>
                          {note.author?.role === 'admin' && (
                            <span className="text-[10px] font-semibold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">Admin</span>
                          )}
                        </div>
                        <span className="text-[10px] text-gray-400">
                          {format(new Date(note.createdAt), 'MMM dd, HH:mm')}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 whitespace-pre-wrap">{note.text}</p>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-400 italic">No notes yet</p>
                )}
              </div>

              {/* Add Note */}
              {(isAdminUser || isReporter) && ticket.status !== 'Closed' && (
                <div className="flex items-start space-x-2">
                  <textarea
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    placeholder="Add a note or update..."
                    rows={2}
                    className="input-field resize-none text-sm flex-1"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleAddNote();
                      }
                    }}
                  />
                  <button
                    onClick={handleAddNote}
                    disabled={!noteText.trim() || submitting}
                    className="btn-primary text-sm h-[42px]"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-100 flex justify-between items-center">
            <div className="flex items-center space-x-2 text-xs text-gray-400">
              <Calendar className="w-3.5 h-3.5" />
              <span>Created {format(new Date(ticket.createdAt), 'MMM dd, yyyy HH:mm')}</span>
              {ticket.updatedAt && (
                <>
                  <span>&bull;</span>
                  <span>Updated {format(new Date(ticket.updatedAt), 'MMM dd, HH:mm')}</span>
                </>
              )}
            </div>
            <div className="flex items-center space-x-2">
              {isAdminUser && ticket.status !== 'Closed' && (
                <button
                  onClick={() => setDeleteConfirm(true)}
                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                  title="Hapus Tiket"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
              <button onClick={onClose} className="btn-secondary text-sm">Close</button>
            </div>
          </div>

          <DeleteConfirmModal
            isOpen={deleteConfirm}
            onClose={() => setDeleteConfirm(false)}
            onConfirm={handleDeleteTicket}
            title="Hapus Tiket"
            isLoading={isDeleting}
          >
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500 font-medium">No. Tiket</span>
              <span className="text-xs font-mono font-bold text-gray-900">{ticket.ticketNumber}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500 font-medium">Judul</span>
              <span className="text-sm font-semibold text-gray-900 text-right max-w-[250px]">{ticket.title}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500 font-medium">Status</span>
              <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full border ${STATUS_COLORS[ticket.status]}`}>{ticket.status}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500 font-medium">Prioritas</span>
              <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full border ${PRIORITY_COLORS[ticket.priority]}`}>{ticket.priority}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500 font-medium">Departemen</span>
              <span className="text-xs font-semibold text-gray-900">{ticket.bagian}</span>
            </div>
          </DeleteConfirmModal>
        </div>
      </div>
    </div>
  );
};

export default TicketDetail;
